import { type FC, useState } from "react";

import { cx } from "@emotion/css";
import { Modal } from "@snapchat/snap-design-system";
import { type GraphQLError } from "graphql";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId } from "../../gql-generated/types";
import { useNotificationApi } from "../../notifications";
import { LensFoldersSearchParams } from "../../shared/hooks";
import { getHostedLensList } from "../api/query/getHostedLensList";
import { useHostedLens } from "../hooks/useHostedLens";

import { boxCSS, containerCss, slideLeftCSS, slideRightCSS } from "./AddHostedLensModal.styles";
import { LensFolderList } from "./LensFolderList";
import { LensFolderListV2 } from "./LensFolderListV2";
import { LensList } from "./LensList";
import { AddHostedLensModalLensFolderTitle, AddHostedLensModalLensTitle, GenerateCTA } from "./messages.generated";
import { useNativeSearchParams } from "./useNativeSearchParams";

interface AddHostedLensModalProps {
    onCancel: () => void;
    visible: boolean;
}

export const AddHostedLensModal: FC<AddHostedLensModalProps> = ({ visible, onCancel }) => {
    const [activeView, setActiveView] = useState<"folder" | "lens">("folder");
    const [selectedLensId, setSelectedLensId] = useState<string | null>(null);
    const { isUpdating, createHostedLens } = useHostedLens();
    const { deleteNativeSearchParams } = useNativeSearchParams();
    const { sendCustomError } = useNotificationApi();

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const handleCancel = () => {
        deleteNativeSearchParams(LensFoldersSearchParams.LensFolder);
        setActiveView("folder");
        onCancel();
    };

    const handleAccept = async (accepted: boolean) => {
        if (!selectedLensId || !accepted) return;
        let errors: readonly GraphQLError[] | undefined;
        try {
            const result = await createHostedLens({
                variables: { lensID: selectedLensId },
                refetchQueries: [getHostedLensList],
            });
            if (result.errors) {
                errors = result.errors;
            }
            handleCancel();
        } catch (error: unknown) {
            errors = [error as GraphQLError];
        }
        if (errors && errors.length > 0) {
            sendCustomError("There was an error generating a Hosted WebAR URL", errors[0]?.message);
        }
    };

    return (
        <Modal
            visible={visible}
            maskClosable={!isUpdating}
            actionText={<GenerateCTA />}
            isActionButtonDisabled={!selectedLensId}
            isActionButtonPending={isUpdating}
            onAction={handleAccept}
            cancelText="Dismiss"
            isCancelButtonDisabled={isUpdating}
            title={activeView === "folder" ? <AddHostedLensModalLensFolderTitle /> : <AddHostedLensModalLensTitle />}
            onCancel={handleCancel}
            width={800}
        >
            <div className={containerCss}>
                {/* If activeView is Lens, we need the LensFolderList to slide in from left */}
                <div className={cx(boxCSS, activeView === "lens" && slideLeftCSS)}>
                    {isMyLensesOrgsV2Enabled ? (
                        <LensFolderListV2 handleView={setActiveView} />
                    ) : (
                        <LensFolderList handleView={setActiveView} />
                    )}
                </div>
                {/* If activeView is folder, we need the LensList to slide in from right */}
                <div className={cx(boxCSS, activeView === "folder" && slideRightCSS)}>
                    {/* Render LensList only if selected, this will cause it to fetch lenses */}
                    {activeView === "lens" && (
                        <LensList
                            handleView={setActiveView}
                            setSelectedLens={setSelectedLensId}
                            selectedLensId={selectedLensId ?? ""}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};
